import React, { useState } from 'react';
import {
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Paper,
  TableContainer,
  Chip,
} from '@mui/material';
import { blue, blueGrey } from '@mui/material/colors';
import { Role, LabourCostRate, LabourBillingRate } from 'api/labourApi';
import BusinessIcon from '@mui/icons-material/Business';
import AccountBoxIcon from '@mui/icons-material/AccountBox';

interface LabourRatesTableComponentProps {
  costRates: LabourCostRate[];
  billingRates: LabourBillingRate[];
  onRoleClick?: (role: Role, rateType: 'cost' | 'billing') => void;
}

// All possible roles
const ALL_ROLES = {
  [Role.Administrator]: 'Administrator',
  [Role.ProjectManager]: 'Project Coordinator',
  [Role.Supervisor]: 'Supervisor',
  [Role.Technician]: 'Technician',
};

export const LabourRatesTableComponent: React.FC<
  LabourRatesTableComponentProps
> = ({ costRates, billingRates, onRoleClick }) => {
  // Get unique branch/client combinations for billing rates
  const getBillingBranchKey = (rate: LabourBillingRate): string => {
    if (rate.branchId && rate.clientId) {
      return `${rate.branch?.name || 'Unknown Branch'}/${
        rate.client?.legalName || 'Unknown Client'
      }`;
    } else if (rate.clientId) {
      return rate.client?.legalName || 'Unknown Client';
    } else if (rate.branchId) {
      return rate.branch?.name || 'Unknown Branch';
    } else if (rate.isDefault) {
      return 'Default';
    }
    return 'Unknown';
  };

  const allBillingBranches = Array.from(
    new Set(billingRates?.map(getBillingBranchKey) || [])
  )
    .filter(Boolean)
    .sort((a, b) => {
      if (a === 'Default') return -1; // "Default" comes first
      if (b === 'Default') return 1; // "Default" comes first
      return a.localeCompare(b); // Sort alphabetically for others
    });

  // Get latest cost rate for a role
  const getLatestCostRateForRole = (role: Role): LabourCostRate | null => {
    const roleCostRates = costRates.filter((r) => r.role === role);
    if (roleCostRates.length === 0) return null;

    return roleCostRates.reduce((latest, current) => {
      return new Date(current.effectiveFrom) > new Date(latest.effectiveFrom)
        ? current
        : latest;
    }, roleCostRates[0]);
  };

  // Get latest billing rate for a role and branch/client combination
  const getLatestBillingRateForRoleAndBranch = (
    role: Role,
    branchKey: string
  ): number | null => {
    const matchingRates = billingRates.filter(
      (rate) => rate.role === role && getBillingBranchKey(rate) === branchKey
    );

    if (matchingRates.length === 0) return null;

    const latestRate = matchingRates.reduce((latest, current) => {
      return new Date(current.effectiveFrom) > new Date(latest.effectiveFrom)
        ? current
        : latest;
    }, matchingRates[0]);

    return Number(latestRate.billingRate);
  };

  return (
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table stickyHeader size="medium">
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                position: 'sticky',
                left: 0,
                zIndex: 5,
                background: blueGrey[50],
                minWidth: 150,
                borderRight: '1px solid #ccc',
                fontWeight: 'bold',
              }}
              rowSpan={2}>
              Role
            </TableCell>
            <TableCell
              sx={{
                background: blueGrey[100],
                color: '#000',
                position: 'sticky',
                top: 0,
                zIndex: 4,
                borderRight: '1px solid #ccc',
                fontWeight: 'bold',
              }}
              align="center"
              colSpan={1}>
              Cost Rates
            </TableCell>

            <TableCell
              sx={{
                background: blue[100],
                color: '#000',
                position: 'sticky',
                top: 0,
                zIndex: 4,
                borderRight: '1px solid #ccc',
                fontWeight: 'bold',
              }}
              align="center"
              colSpan={allBillingBranches.length || 1}>
              Billing Rates
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell
              sx={{
                background: blueGrey[50],
                minWidth: 120,
                zIndex: 4,
                position: 'sticky',
                top: 57,
                borderRight: '1px solid #ccc',
                fontWeight: 'bold',
              }}
              align="center">
              Rate
            </TableCell>

            {allBillingBranches.length > 0 ? (
              allBillingBranches.map((b) => (
                <TableCell
                  sx={{
                    background: blue[50],
                    minWidth: 150,
                    zIndex: 4,
                    position: 'sticky',
                    top: 57,
                    borderRight: '1px solid #ccc',
                  }}
                  key={b}
                  align="center">
                  {b.split('/').map((part, index) => (
                    <Chip
                      key={`${b}-${index}`}
                      label={part}
                      sx={{
                        margin: '2px',
                        color: '#000',
                        backgroundColor: blue[200],
                        paddingInline: '0.5rem',
                        paddingY: '1rem',
                      }}
                      size="small"
                      icon={
                        index === 0 ? (
                          <BusinessIcon
                            color="action"
                            sx={{ fontSize: '1rem', color: '#000' }}
                          />
                        ) : (
                          <AccountBoxIcon
                            color="action"
                            sx={{ fontSize: '1rem', color: '#000' }}
                          />
                        )
                      }
                    />
                  ))}
                </TableCell>
              ))
            ) : (
              <TableCell
                sx={{
                  background: blue[50],
                  zIndex: 4,
                  position: 'sticky',
                  top: 57,
                  borderRight: '1px solid #ccc',
                }}
                align="center">
                <Typography variant="caption">
                  No Billing Rates Added Yet
                </Typography>
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.keys(ALL_ROLES).map((roleKey) => {
            const role = roleKey as Role;
            const costRate = getLatestCostRateForRole(role);

            return (
              <TableRow key={role} hover>
                <TableCell
                  sx={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 2,
                    background: '#fff',
                    minWidth: 150,
                    borderRight: '1px solid #ccc',
                    fontWeight: 'bold',
                  }}>
                  {ALL_ROLES[role]}
                </TableCell>

                <TableCell
                  align="center"
                  sx={{
                    borderRight: '1px solid #ccc',
                    cursor: costRate ? 'pointer' : 'default',
                  }}
                  onClick={() => costRate && onRoleClick?.(role, 'cost')}>
                  {costRate ? `$${Number(costRate.rate).toFixed(2)}` : '-'}
                </TableCell>

                {allBillingBranches.length > 0 ? (
                  allBillingBranches.map((b) => {
                    const billingRate = getLatestBillingRateForRoleAndBranch(
                      role,
                      b
                    );
                    return (
                      <TableCell
                        key={b}
                        align="center"
                        sx={{
                          borderRight: '1px solid #ccc',
                          cursor: billingRate ? 'pointer' : 'default',
                        }}
                        onClick={() =>
                          billingRate && onRoleClick?.(role, 'billing')
                        }>
                        {billingRate !== null
                          ? `$${billingRate.toFixed(2)}`
                          : '-'}
                      </TableCell>
                    );
                  })
                ) : (
                  <TableCell
                    align="center"
                    sx={{ borderRight: '1px solid #ccc' }}>
                    -
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
